///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { SstOverlay } from "@/earthbrowser/dataset-distribution/sst/SstOverlay"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { time, timedelta } from "@stovokor/math/time"
import { DatasetLayer, type ArrowRow, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { SstTable, type SstFrameRecord } from "./SstTable"

// Frames are daily analyses (~1 day ingest latency). Tier frame limits per
// the Phase 4 depth gates (SUBSCRIPTION_PRICING_MODEL.md): the newest frame
// plus enough history to cover the tier's window (2h free / 48h paid)
// despite the latency. The former month-deep retention exceeded even the
// paid 48-hour grant; restore it if a longer time-series entitlement ships.
const cPaidFrameLimit = 4
const cFreeFrameLimit = 2

/** Effective frame limit for the current entitlement tier */
function frameLimit(): number {
	// No store (embed builds) means free-tier caps
	return isPaidEntitlement() ? cPaidFrameLimit : cFreeFrameLimit
}

/** Shape of an sst Arrow row (only fields we read) */
type SstRow = {
	id: string
	product: string
	data_timestamp: string
	slot_timestamp: string
	image_url: string
	source_path: string
	size_bytes: number
	width: number
	height: number
	vmin: number
	vmax: number
	bounds_wgs84: string | null | undefined
	created_at: string
}

/**
 * Global sea surface temperature layer from NOAA Coral Reef Watch CoralTemp.
 * One equirectangular frame per day; the overlay drapes a thermal-palette
 * colormap over the oceans (land is nodata/transparent).
 */
export class SstDatasetLayer extends DatasetLayer<SstFrameRecord> {
	private sstOverlay: SstOverlay
	private earth: Earth
	// Cache frames by URL to avoid reloading unchanged frames
	private frameCache = new Map<string, RadarTextureFrame>()
	// Redux state subscription unsubscribe function
	private unsubscribe: (() => void) | null = null
	// Last seen entitlement tier, to re-query frames when depth changes
	// (e.g. checkout completes and the 48-hour window unlocks live)
	private lastPaidState = false

	constructor(context: EarthBrowserContext, earth: Earth) {
		super(context, "sst")
		this.sstOverlay = new SstOverlay(context)
		this.earth = earth

		// Initialize visibility based on current state
		const initialVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("sst") ?? false
		this.visible = initialVisible
		this.lastPaidState = isPaidEntitlement()
		if (initialVisible) {
			this.earth.addOverlay(this.sstOverlay)
		}

		// Subscribe to visibility and entitlement state changes
		this.unsubscribe = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			if (!state) return
			this.setVisible(state.earthbrowser.visibleDatasets.includes("sst"))
			if (state.entitlements.isPaid !== this.lastPaidState) {
				this.lastPaidState = state.entitlements.isPaid
				void this.refresh()
			}
		})
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "sst",
			// One new frame per day (~1 day latency), so hourly polling is plenty
			pollIntervalMs: 3_600_000,
			needsSync: true,
			supportsDelta: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await SstTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)
		const frames = this.parseRows(rows)
		await SstTable.replaceAll(frames)
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const upserts = this.parseRows(upsertRows)
		if (upserts.length > 0) {
			await SstTable.upsert(upserts)
		}
	}

	private parseRows(arrowRows: ArrowRow[]): SstFrameRecord[] {
		const rows = arrowRows as SstRow[]
		return rows.map(r => ({
			id: String(r.id),
			product: String(r.product),
			data_timestamp: String(r.data_timestamp),
			slot_timestamp: String(r.slot_timestamp),
			image_url: String(r.image_url),
			source_path: String(r.source_path),
			size_bytes: Number(r.size_bytes),
			width: Number(r.width),
			height: Number(r.height),
			vmin: Number(r.vmin),
			vmax: Number(r.vmax),
			bounds_wgs84: r.bounds_wgs84 === null || r.bounds_wgs84 === undefined ? null : String(r.bounds_wgs84),
			created_at: String(r.created_at),
		}))
	}

	public async queryFeatures(): Promise<SstFrameRecord[]> {
		const rows = await SstTable.getRecent(frameLimit())
		return rows
	}

	public createSceneObject(_feature: SstFrameRecord): DatasetSceneObject<SstFrameRecord> | undefined {
		// SST frames are consumed by overlay rendering, not by creating scene objects.
		return undefined
	}

	public async onSyncComplete(): Promise<void> {
		await this.refresh()
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.sstOverlay.getTimeRange()
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		return this.sstOverlay.getTimeLaneDetail()
	}

	protected override frameLoadState(): LayerLoadState | null {
		return this.sstOverlay.getLoadState()
	}

	/**
	 * Refresh SST frames from the database and update the overlay.
	 * Uses delta calculation to reuse existing frames and only load new ones.
	 */
	public override async refresh(): Promise<void> {
		try {
			const records = await SstTable.getRecent(frameLimit())
			const textureFrames = this.updateFrameCache(records)
			this.sstOverlay.setFrames(textureFrames)
		} catch (error) {
			console.error("[SstDatasetLayer] Failed to refresh:", error)
		}
	}

	/**
	 * Update the frame cache with new records.
	 * - Reuses existing frames for URLs that are still valid
	 * - Creates new frames for new URLs
	 * - Purges frames for URLs no longer in the record set
	 */
	private updateFrameCache(records: SstFrameRecord[]): RadarTextureFrame[] {
		// Records are sorted desc by slot_timestamp, we want chronological order
		const sortedRecords = [...records].reverse()

		// Build set of URLs from current records
		const currentUrls = new Set(sortedRecords.map(r => r.image_url))

		// Find and purge frames that are no longer needed
		const urlsToRemove: string[] = []
		for (const [url, frame] of this.frameCache) {
			if (!currentUrls.has(url)) {
				frame.purge()
				urlsToRemove.push(url)
			}
		}
		for (const url of urlsToRemove) {
			this.frameCache.delete(url)
		}

		// Build the frames array, reusing cached frames or creating new ones
		const frames: RadarTextureFrame[] = sortedRecords.map(record => {
			const existingFrame = this.frameCache.get(record.image_url)
			if (existingFrame) {
				return existingFrame
			}

			const startTime = new time(record.slot_timestamp)
			const duration = timedelta.hours(24)
			// wrapLongitude: frames are global, so filtering must wrap at the
			// antimeridian instead of clamping
			const newFrame = new RadarTextureFrame(this.context, record.image_url, startTime, duration, true)
			this.frameCache.set(record.image_url, newFrame)
			return newFrame
		})

		return frames
	}

	/**
	 * Set visibility of the SST layer
	 */
	public setVisible(visible: boolean): void {
		if (this.visible === visible) return

		this.visible = visible

		if (visible) {
			this.earth.addOverlay(this.sstOverlay)
		} else {
			this.earth.removeOverlay(this.sstOverlay)
			// Reclaim GPU memory while hidden: purge the series (resets its
			// display state) plus any cached frames outside the series;
			// frames reload from the SQLite image cache when re-enabled
			this.sstOverlay.purge()
			for (const frame of this.frameCache.values()) {
				frame.purge()
			}
		}
	}

	override dispose(): void {
		// Unsubscribe from Redux state changes
		if (this.unsubscribe) {
			this.unsubscribe()
			this.unsubscribe = null
		}

		// Purge all cached frames
		for (const frame of this.frameCache.values()) {
			frame.purge()
		}
		this.frameCache.clear()

		this.earth.removeOverlay(this.sstOverlay)
		super.dispose()
	}
}
